import { parseISO } from 'date-fns';

import { parseFormNumber, roundToPrecision } from '@/helpers/finance';
import { mapClientPaymentTermsToDTO } from '@/modules/clients/mappers/client-payment-terms.mapper';
import { PaymentTerms } from '@/modules/clients/types/client';
import { normalizeEmailList } from '@/modules/common/validations/email-list-schema';
import {
  FINANCE_ROUNDING_EFFECTIVE_RATE_DECIMAL_PLACES,
  FINANCE_ROUNDING_TOTAL_DECIMAL_PLACES,
} from '@/modules/finance/common/constants/rounding.constants';
import {
  formatDateToNonISOFormat,
  normalizeToStartOfDayUTC,
  parseCalendarDate,
} from '@/utils/date-formatters';

import { InvoiceDetails, InvoiceEntityType } from '../types';
import { InvoiceEntityTypeDTO } from '../types/dto/invoice-entity-type.dto';
import { UpdateInvoiceRequest } from '../types/dto/request';
import { UpdateInvoiceFormValues, UpdateInvoiceItemFormValues } from '../validations';

const mapEntityTypeToDTO = (entityType: InvoiceEntityType): InvoiceEntityTypeDTO => {
  switch (entityType) {
    case InvoiceEntityType.User:
      return InvoiceEntityTypeDTO.USER;
    case InvoiceEntityType.Overhead:
      return InvoiceEntityTypeDTO.OVERHEAD;
    case InvoiceEntityType.Unknown:
      return InvoiceEntityTypeDTO.UNKNOWN;
  }
};

const getItemAmount = (item: UpdateInvoiceItemFormValues): string => {
  if (item.entityType !== InvoiceEntityType.User && item.entityType !== InvoiceEntityType.Overhead)
    return item.amount;

  return roundToPrecision(
    parseFormNumber(item.quantity) * parseFormNumber(item.unitPrice),
    FINANCE_ROUNDING_TOTAL_DECIMAL_PLACES
  ).toString();
};

const getItemOrder = (entityType: InvoiceEntityType): number => {
  if (entityType === InvoiceEntityType.User) return 0;
  if (entityType === InvoiceEntityType.Overhead) return 1;
  return 2;
};

/** Creates RHF defaults from the server snapshot, without deriving server-owned fields. */
export const mapInvoiceDetailsToUpdateFormValues = (
  details: InvoiceDetails
): UpdateInvoiceFormValues => ({
  period: {
    from: details.startDate ? parseISO(details.startDate) : undefined,
    to: details.endDate ? parseISO(details.endDate) : undefined,
  },
  issuedDate: parseCalendarDate(details.issuedDate),
  paymentTerm: details.paymentTerm ?? '',
  currencyCode: details.currencyCode,
  paymentDetailsId: details.paymentDetailsId,
  receiverEmails: {
    to: normalizeEmailList(details.receiverEmails.to),
    cc: normalizeEmailList(details.receiverEmails.cc),
    bcc: normalizeEmailList(details.receiverEmails.bcc),
  },
  items: [...details.items]
    .sort((first, second) => getItemOrder(first.entityType) - getItemOrder(second.entityType))
    .map(item => ({
      entityType: item.entityType,
      entityId: item.entityId?.toString() ?? '',
      description: item.description ?? '',
      quantity: item.quantity ?? '',
      unitPrice: item.unitPrice ?? '',
      amount: item.amount,
    })),
});

/** Maps editable form state to the full replacement API payload. */
export const mapUpdateInvoiceFormToRequest = ({
  details,
  values,
}: {
  details: InvoiceDetails;
  values: UpdateInvoiceFormValues;
}): UpdateInvoiceRequest => {
  if (!values.period.from || !values.period.to || !values.issuedDate) {
    throw new Error('Validated invoice dates are required to build the update request');
  }

  return {
    syncToken: details.syncToken,
    startDate: formatDateToNonISOFormat(normalizeToStartOfDayUTC(values.period.from)),
    endDate: formatDateToNonISOFormat(normalizeToStartOfDayUTC(values.period.to)),
    issuedDate: formatDateToNonISOFormat(normalizeToStartOfDayUTC(values.issuedDate)),
    paymentTerm: mapClientPaymentTermsToDTO(values.paymentTerm as PaymentTerms),
    currencyCode: values.currencyCode,
    paymentDetailsId: values.paymentDetailsId,
    receiverEmails: { ...values.receiverEmails },
    items: values.items.map(item => ({
      entityType: mapEntityTypeToDTO(item.entityType),
      ...(item.entityId ? { entityId: Number(item.entityId) } : {}),
      ...(item.entityType !== InvoiceEntityType.Overhead && item.quantity
        ? { quantity: item.quantity }
        : {}),
      ...(item.entityType !== InvoiceEntityType.Overhead && item.unitPrice
        ? {
            unitPrice: roundToPrecision(
              parseFormNumber(item.unitPrice),
              FINANCE_ROUNDING_EFFECTIVE_RATE_DECIMAL_PLACES
            ).toString(),
          }
        : {}),
      ...(item.description ? { description: item.description.trim() } : {}),
      amount: getItemAmount(item),
    })),
  };
};
